// What a page shows while its first data arrives (Ruben, 2 Nov 2026: the tab
// switch looked broken when the footer rose into an empty page; 4 Nov 2026:
// « add a loading state … do it professionally »). Each tab gets the outline
// of its own page, built from the same layout classes as the real one (.ph,
// .grid.g4, .md, .livewrap …), so the real page lands exactly where its
// outline was: no jump, on a phone too. The outline fades in late, so a fast
// load never flashes it, and every block shimmers with one shared sweep.
import { S } from '../lib/strings';

type Len = number | string;

/** One grey block. */
export const Sk = ({ w = '100%', h = 12, r, className }: { w?: Len; h?: Len; r?: Len; className?: string }) => (
  <i className={'sk' + (className ? ' ' + className : '')} style={{ width: w, height: h, borderRadius: r }} />
);

/** The page title, its line under it and, on the right, the page's controls. */
export const SkHead = ({ right, eyebrow }: { right?: Len[]; eyebrow?: boolean }) => (
  <div className="ph">
    <div className="sk-ph">{eyebrow && <Sk w={150} h={10} />}<Sk w={200} h={30} r={10} /><Sk w="min(380px, 64vw)" h={13} /></div>
    {right && <div className="r">{right.map((w, i) => <Sk key={i} w={w} h={40} r={999} />)}</div>}
  </div>
);

const SkKpi = ({ h = 150 }: { h?: number }) => (
  <div className="card sk-card" style={{ minHeight: h }}>
    <div className="sk-line"><Sk w={30} h={30} r={10} /><Sk w="44%" h={11} /></div>
    <Sk w="34%" h={34} r={9} />
    <Sk w="62%" h={11} />
  </div>
);
export const SkKpis = ({ n = 4, h }: { n?: number; h?: number }) => (
  <div className={'grid ' + (n === 4 ? 'g4' : 'sk-g' + n)}>{Array.from({ length: n }, (_, i) => <SkKpi key={i} h={h} />)}</div>
);

/** A card with a title and a few lines of decreasing length. */
export const SkCard = ({ h, lines = 4, title = true, className, children }: { h?: number; lines?: number; title?: boolean; className?: string; children?: React.ReactNode }) => (
  <div className={'card sk-card' + (className ? ' ' + className : '')} style={{ minHeight: h }}>
    {title && <Sk w="28%" h={14} />}
    {children ?? Array.from({ length: lines }, (_, i) => <Sk key={i} w={`${92 - i * 11}%`} />)}
  </div>
);

/** Filter chips over a table. */
export const SkTable = ({ rows = 8, cols = 5, chips = 4, search = true }: { rows?: number; cols?: number; chips?: number; search?: boolean }) => (
  <div className="card">
    {(chips > 0 || search) && (
      <div className="fbar">{Array.from({ length: chips }, (_, i) => <Sk key={i} w={i ? 92 : 64} h={30} r={999} />)}{search && <Sk w={220} h={34} r={999} className="sk-push" />}</div>
    )}
    <div className="sk-tbl">
      {Array.from({ length: rows }, (_, i) => (
        <div className="sk-tr" key={i} style={{ gridTemplateColumns: `minmax(0,2.2fr) repeat(${cols - 1}, minmax(0,1fr))` }}>
          <div className="sk-line"><Sk w={34} h={34} r={999} /><div className="sk-col"><Sk w="70%" h={12} /><Sk w="44%" h={10} /></div></div>
          {Array.from({ length: cols - 1 }, (_, j) => <Sk key={j} w={`${70 - ((i + j) % 3) * 14}%`} h={12} />)}
        </div>
      ))}
    </div>
  </div>
);

/** A list of rows: a thumbnail, two lines and a figure on the right. */
export const SkList = ({ rows = 6, thumb = 'round' }: { rows?: number; thumb?: 'round' | 'bottle' }) => (
  <div className="sk-list">
    {Array.from({ length: rows }, (_, i) => (
      <div className="sk-li" key={i}>
        {thumb === 'bottle' ? <Sk w={22} h={44} r={7} /> : <Sk w={40} h={40} r={999} />}
        <div className="sk-col"><Sk w={`${66 - (i % 3) * 10}%`} h={13} /><Sk w="40%" h={10} /></div>
        <div className="sk-col sk-end"><Sk w={42} h={14} /><Sk w={58} h={10} /></div>
      </div>
    ))}
  </div>
);

/** A detail panel (a home, a rider, a brand): a header card and two cards of lines. */
export const SkDetail = () => (
  <div className="hd" aria-hidden="true">
    <div className="card sk-card" style={{ minHeight: 210 }}>
      <div className="sk-line"><Sk w={56} h={56} r={18} /><div className="sk-col"><Sk w="42%" h={20} r={7} /><Sk w="26%" h={11} /></div></div>
      <div className="sk-g3">{[0, 1, 2].map((i) => <div key={i} className="sk-col"><Sk w="50%" h={10} /><Sk w="70%" h={24} r={8} /></div>)}</div>
    </div>
    <SkCard h={220} lines={5} />
    <SkCard h={180} lines={3} />
  </div>
);

export type Page = 'ov' | 'live' | 'ord' | 'hh' | 'dev' | 'fin' | 'mk' | 'team' | 'day' | 'account' | 'a-home' | 'a-day' | 'a-orders' | 'a-account' | 'page';

function body(p: Page) {
  switch (p) {
    case 'ov': return <>
      <SkHead />
      <SkKpis />
      <div className="grid g-ov sk-gap"><div className="card sk-card sk-map" style={{ minHeight: 452 }}><Sk w={120} h={28} r={999} /></div><SkCard h={452} lines={7} /></div>
      <div className="grid g-ov sk-gap"><SkCard h={350} lines={6} /><SkCard h={350} lines={5} /></div>
    </>;
    case 'live': return (
      <div className="livewrap sk-live">
        <div className="sk-map sk-fill" />
        <div className="lpanel card sk-card"><Sk w="50%" h={22} r={8} /><Sk w="80%" h={11} /><Sk w="100%" h={36} r={999} /><SkList rows={6} /></div>
      </div>
    );
    case 'ord': return <><SkHead right={[180]} /><SkTable rows={9} cols={6} chips={5} /></>;
    case 'hh': return <>
      <SkHead />
      <div className="md">
        <div className="card hlist sk-card"><Sk h={40} r={999} /><div className="sk-line">{[56, 84, 92, 96].map((w, i) => <Sk key={i} w={w} h={28} r={999} />)}</div><SkList rows={7} thumb="bottle" /></div>
        <SkDetail />
      </div>
    </>;
    case 'dev': return <><SkHead /><SkKpis /><div className="sk-gap"><SkTable rows={8} cols={6} chips={5} search={false} /></div></>;
    case 'fin': return <>
      <SkHead right={[150, 40]} />
      <div className="card sk-card" style={{ minHeight: 408 }}>
        <Sk w={190} h={10} /><Sk w={300} h={56} r={12} />
        <div className="sk-g4">{[0, 1, 2, 3].map((i) => <div key={i} className="sk-col"><Sk w="55%" h={10} /><Sk w="70%" h={20} r={7} /></div>)}</div>
        <Sk h={200} r={14} className="sk-chart" />
      </div>
      <div className="sk-gap"><SkCard h={455} lines={8} /></div>
    </>;
    case 'mk': return <>
      <SkHead right={[160, 100]} />
      <SkKpis n={3} h={197} />
      <div className="sk-gap"><SkKpis n={3} h={264} /></div>
      <div className="sk-gap"><SkCard h={112} title={false}><div className="sk-strip">{Array.from({ length: 10 }, (_, i) => <Sk key={i} w={86} h={78} r={14} />)}</div></SkCard></div>
    </>;
    case 'team': return <>
      <SkHead right={[186]} eyebrow />
      <div className="sk-g4 sk-att">{[0, 1, 2, 3].map((i) => <SkCard key={i} h={130} lines={2} />)}</div>
      <div className="sk-gap"><SkCard h={284} title={false}><SkList rows={3} /></SkCard></div>
      <div className="sk-gap"><SkTable rows={7} cols={6} chips={3} search={false} /></div>
    </>;
    case 'day': return <><div className="sk-g4">{[0, 1, 2, 3].map((i) => <SkKpi key={i} h={120} />)}</div><div className="sk-gap"><SkCard h={420} lines={8} /></div></>;
    case 'account': return <div className="sk-acc"><div className="sk-col sk-grow"><SkCard h={360} lines={2}><Sk h={240} r={14} className="sk-chart" /></SkCard><SkCard h={300} lines={6} /></div><div className="sk-col sk-side"><SkCard h={300} lines={4} /><SkCard h={260} lines={5} /></div></div>;
    case 'a-home': return <><SkHead eyebrow /><div className="sk-acc"><SkCard h={260} lines={4} className="sk-grow" /><div className="sk-col sk-side"><SkKpi h={122} /><SkKpi h={122} /></div></div><div className="sk-gap sk-g3">{[0, 1, 2].map((i) => <SkCard key={i} h={300} lines={5} />)}</div></>;
    case 'a-day': return <><SkHead eyebrow /><div className="sk-g4">{[0, 1, 2, 3].map((i) => <SkKpi key={i} h={120} />)}</div><div className="sk-gap"><SkCard h={420} lines={8} /></div></>;
    case 'a-orders': return <><SkHead eyebrow /><SkTable rows={8} cols={5} chips={3} search={false} /></>;
    case 'a-account': return <><SkHead eyebrow /><div className="sk-acc"><div className="sk-col sk-grow"><SkCard h={360} lines={2}><Sk h={240} r={14} className="sk-chart" /></SkCard><SkCard h={300} lines={6} /></div></div></>;
    default: return <><SkHead /><SkKpis n={3} /><div className="sk-gap"><SkCard h={320} lines={5} /></div></>;
  }
}

/** The outline of a page while its first data arrives. `inline` for a part of a page (a panel, a sub-view). */
export function Loading({ page = 'page', inline }: { page?: Page; inline?: boolean }) {
  return (
    <section className={(inline ? 'skel in' : 'view on skel')} aria-busy="true" aria-label={S.loading}>
      {body(page)}
    </section>
  );
}
